/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Heading, HFlow, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { Divider } from 'components/divider/Divider'
import { HLabel } from 'components/HLabel'
import { useProblemasModalSimplificadoQuery } from 'graphql/hooks.generated'
import { Problema } from 'graphql/types.generated'
import { Fragment } from 'react'
import { isAfterOrEqual } from 'util/date/compare'
import { formatDate, toDate } from 'util/date/formatDate'
import { humanizeDate } from 'util/date/humanize-date'
import { SituacaoProblemaTag } from 'view/atendimentos/detail/components/SituacaoProblemaTag'

import { AcompanhamentoAccordionCard } from '../../components/AcompanhamentoAccordionCard'
import { sortProblemasPreNatal } from '../util-acompanhamentoPreNatal'

interface ListaProblemasCondicoesCardProps {
  prontuarioId: ID
  dataInicioGestacao: Date
}

export function ListaProblemasCondicoesCard(props: ListaProblemasCondicoesCardProps) {
  const { prontuarioId, dataInicioGestacao } = props

  const theme = useTheme()
  const styles = createStyles(theme)

  const {
    loading,
    data: { problemas },
  } = useProblemasModalSimplificadoQuery({
    variables: { filtro: { prontuarioId } },
  })

  const sortedProblemas = problemas?.content?.sort(sortProblemasPreNatal)
  const problemasToShow = sortedProblemas?.slice(0, 2)
  const problemasAccordion = sortedProblemas?.slice(2)

  const renderProblemaRow = (index: number, problema: Problema) => {
    const { ciap, cid10, ultimaEvolucao, situacao } = problema
    const humanizedStartDate = ultimaEvolucao.dataInicio && humanizeDate(ultimaEvolucao.dataInicio)
    const hasIgDeInicio =
      ultimaEvolucao.dataInicio && isAfterOrEqual(toDate(ultimaEvolucao.dataInicio), dataInicioGestacao)
    const igDeInicio = hasIgDeInicio && humanizeDate(ultimaEvolucao.dataInicio, dataInicioGestacao?.getTime())

    return (
      <Fragment>
        {!!index && <Divider />}
        <HFlow
          justifyContent='space-between'
          alignItems='center'
          style={css`
            padding: 0.5rem;
          `}
        >
          <VFlow vSpacing={0.125}>
            <Heading level={4} style={styles.separator}>
              {ciap && (
                <Fragment>
                  {ciap.descricao} <Text fontWeight='initial'>(CIAP2 {ciap.codigo})</Text>
                </Fragment>
              )}
              {cid10 && (
                <Fragment>
                  {cid10.nome} <Text fontWeight='initial'>(CID10 {cid10.codigo})</Text>
                </Fragment>
              )}
            </Heading>
            <HFlow hSpacing={0.5}>
              <HLabel title='Início:'>
                {ultimaEvolucao.dataInicio &&
                  `${formatDate(ultimaEvolucao.dataInicio)} | ${
                    humanizedStartDate === 'Hoje' ? humanizedStartDate : `Há ${humanizedStartDate}`
                  }`}
              </HLabel>
              <HLabel title='Última avaliação:'>
                {ultimaEvolucao.atualizadoEm && formatDate(ultimaEvolucao.atualizadoEm)}
              </HLabel>
            </HFlow>
            <HLabel title='IG de início:'>{igDeInicio === 'Hoje' ? '0 dias' : igDeInicio}</HLabel>
          </VFlow>
          <SituacaoProblemaTag situacao={situacao} />
        </HFlow>
      </Fragment>
    )
  }

  return (
    <AcompanhamentoAccordionCard
      id='lista-problemas-condicoes'
      title='Lista de problemas e condições'
      itemName='problema'
      loading={loading}
      items={problemasToShow}
      accordionItems={problemasAccordion}
      renderRow={(index, problema) => renderProblemaRow(index, problema as Problema)}
    />
  )
}

const createStyles = (theme: Theme) => ({
  separator: css`
    & > :not(:last-child):after {
      content: '|';
      margin: 0 0.25rem;
    }
  `,
})
